import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Wrapper = styled.div`
  padding: 1rem;
`;

const ChartContainer = styled.div`
  height: 70vh;
  padding: 1rem;
`;

const FiltersWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  z-index: 10;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

const ChartWrapper = styled.div`
  position: relative;
`;

const monthNamesPL = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const PorownanieSrednichDziennychZakupow = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [chartData, setChartData] = useState(null);

  const calculateZakup = (row) => {
    const v = parseFloat(row.K_42);
    return Number.isFinite(v) ? v : 0;
  };

  // init years & default selection
  useEffect(() => {
    const years = new Set();
    const months = new Set();

    (data || []).forEach((row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      if (!isNaN(year) && !isNaN(month)) {
        years.add(year);
        months.add(month);
      }
    });

    const sortedYears = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sortedYears);

    if (!selectedYear && sortedYears.length > 0) {
      const lastYear = sortedYears[sortedYears.length - 1];
      setSelectedYear(lastYear);

      const candidateMonths = Array.from(months)
        .filter((m) =>
          (data || []).some(
            (d) => parseInt(d.Rok, 10) === lastYear && parseInt(d.Miesiac, 10) === m
          )
        )
        .sort((a, b) => b - a);
      if (candidateMonths.length > 0) setSelectedMonth(candidateMonths[0]);
    }
  }, [data]);

  // update months for selected year
  useEffect(() => {
    if (!selectedYear) return;
    const months = new Set();
    (data || []).forEach((row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      if (year === selectedYear && !isNaN(month)) months.add(month);
    });
    setAvailableMonths(Array.from(months).sort((a, b) => a - b));
  }, [selectedYear, data]);

  // build chart
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const dailyMap = {};

    (data || []).forEach((row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      const rawDate = row.DataZakupu;
      const date = rawDate ? new Date(rawDate).toISOString().split("T")[0] : null;

      if (year === selectedYear && month === selectedMonth && date) {
        if (!dailyMap[date]) dailyMap[date] = 0;
        dailyMap[date] += calculateZakup(row);
      }
    });

    const dates = Object.keys(dailyMap).sort();
    const rawValues = dates.map((d) => dailyMap[d]);

    // Compute average using only non-negative values
    const nonNegative = rawValues.filter((v) => v >= 0);
    const avg = nonNegative.length
      ? nonNegative.reduce((a, b) => a + b, 0) / nonNegative.length
      : 0;

    // Hide negatives by setting them to null; Chart.js won't render those bars
    const values = rawValues.map((v) => (v < 0 ? null : v));

    // Colors: red < avg, green >= avg; null bars stay transparent
    const backgroundColors = values.map((v) => {
      if (v === null) return "rgba(0,0,0,0)";
      return v < avg ? "rgba(220, 53, 69, 0.85)" : "rgba(46, 125, 50, 0.85)";
    });
    const borderColors = values.map((v) => {
      if (v === null) return "rgba(0,0,0,0)";
      return v < avg ? "rgba(220, 53, 69, 1)" : "rgba(46, 125, 50, 1)";
    });

    setChartData({
      labels: dates,
      datasets: [
        {
          label: "Wartość dzienna (PLN)",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1
        },
        {
          label: `Średnia: ${avg.toLocaleString("pl-PL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`,
          data: Array(dates.length).fill(avg),
          type: "line",
          borderColor: "blue",
          borderWidth: 1,
          pointRadius: 0,
          fill: false
        }
      ]
    });
  }, [selectedYear, selectedMonth, data]);

  return (
    <Wrapper>
      <ChartWrapper>
        <InfoIconWrapper data-tooltip-id="avg-compare-tooltip">
          <Info />
        </InfoIconWrapper>

        <InfoTooltip
          id="avg-compare-tooltip"
          place="left"
          content={
            <>
              <strong>Porównanie średnich dziennych zakupów</strong>
              <br />
              Zielony: ≥ średnia miesiąca. Czerwony: &lt; średniej.
              <br />
              Dni z wartością &lt; 0 są pomijane.
            </>
          }
        />

        <FiltersWrapper>
          <div>
            <label>Rok:&nbsp;</label>
            <select
              value={selectedYear || ""}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Miesiąc:&nbsp;</label>
            <select
              value={selectedMonth || ""}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
            >
              {availableMonths.map((month) => (
                <option key={month} value={month}>{monthNamesPL[month - 1]}</option>
              ))}
            </select>
          </div>
        </FiltersWrapper>

        <ChartContainer>
          {chartData ? (
            <>
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: `Porównanie średnich dziennych zakupów – ${monthNamesPL[selectedMonth - 1]} ${selectedYear}`
                    },
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          const y = ctx.parsed.y;
                          if (y === null || typeof y === "undefined") return null;
                          return `${ctx.dataset.label}: ${y.toLocaleString("pl-PL", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "Zakupy (PLN)" }
                    }
                  }
                }}
              />
              <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "12px" }}>
                <div>
                  <span style={{ backgroundColor: "rgba(46, 125, 50, 0.85)", padding: "4px 8px", marginRight: 6 }} />
                  Powyżej / równo średniej
                </div>
                <div>
                  <span style={{ backgroundColor: "rgba(220, 53, 69, 0.85)", padding: "4px 8px", marginRight: 6 }} />
                  Poniżej średniej
                </div>
                <div>
                  <span style={{ backgroundColor: "blue", padding: "4px 8px", marginRight: 6 }} />
                  Linia średniej
                </div>
              </div>
            </>
          ) : (
            <p>Ładowanie danych...</p>
          )}
        </ChartContainer>
      </ChartWrapper>
    </Wrapper>
  );
};

export default PorownanieSrednichDziennychZakupow;
